const CONTEXT_FIELDS = ["area", "search", "food", "budget", "atmosphere", "transport", "walking", "group", "dates", "needs"];
const REFERENT_KINDS = ["explanation", "venue_result_set", "event_result_set", "night_plan", "participating_venue_set", "admin_report", "admin_priority_set"];
const REFERENT_OPERATIONS = ["explain", "continue", "select", "show_more", "nearer", "cheaper", "more_expensive", "quieter", "livelier", "directions", "refine_date", "largest_drop"];

function envelopeLegacySource(value = {}) {
  if (value.version !== 2) return value;
  const conversation = value.conversation || {};
  const constraints = value.constraints || {};
  return {
    journey_id: conversation.journey_id,
    language: conversation.language,
    ...Object.fromEntries(CONTEXT_FIELDS.map((key) => [key, constraints[key]])),
    venues_only: constraints.venues_only,
    recommendation: value.recommendation,
    night_plan: value.night_plan,
    event_search: value.event_search,
    location: value.location,
    pending_action: value.pending_action,
  };
}

function safeIds(items, limit = 120) {
  return [...new Set((Array.isArray(items) ? items : []).filter((id) => typeof id === "string" && id.length > 0 && id.length <= 120))].slice(0, limit);
}

export function publicConversationContext(value = {}) {
  const source = envelopeLegacySource(value);
  const state = source.recommendation || {};
  const plan = source.night_plan || {};
  const events = source.event_search || {};
  const location = source.location || {};
  const pending = source.pending_action || {};
  return {
    journey_id: /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(source.journey_id || "")) ? String(source.journey_id).toLowerCase() : "",
    language: source.language === "es" ? "es" : "en",
    ...Object.fromEntries(CONTEXT_FIELDS.map((key) => [key, String(source[key] || "").slice(0, 160)])),
    recommendation: {
      area: String(state.area || "").slice(0, 160),
      category: ["restaurant", "bar", "club", "live_music"].includes(state.category) ? state.category : "",
      food: String(state.food || "").slice(0, 160),
      shown_venue_ids: safeIds(state.shown_venue_ids),
      excluded_venue_ids: safeIds(state.excluded_venue_ids),
      ...(safeIds(state.location_excluded_venue_ids, 12).length
        ? { location_excluded_venue_ids: safeIds(state.location_excluded_venue_ids, 12) }
        : {}),
      current_venue_ids: safeIds(state.current_venue_ids, 6),
      selected_venue_id: String(state.selected_venue_id || "").slice(0, 120),
      origin_venue_id: String(state.origin_venue_id || "").slice(0, 120),
      matched_count: Number.isInteger(state.matched_count) && state.matched_count >= 0 ? Math.min(state.matched_count, 10000) : 0,
      remaining_count: Number.isInteger(state.remaining_count) && state.remaining_count >= 0 ? Math.min(state.remaining_count, 10000) : 0,
      has_more: state.has_more === true,
    },
    night_plan: {
      date_anchor: /^\d{4}-\d{2}-\d{2}$/.test(plan.date_anchor || "") ? plan.date_anchor : "",
      date_expression: String(plan.date_expression || "").slice(0,160),
      locked_roles: (Array.isArray(plan.locked_roles) ? plan.locked_roles : []).filter(role => ["dinner", "drinks", "live_music"].includes(role)).slice(0,3),
      dinner_venue_id: String(plan.dinner_venue_id || "").slice(0, 120),
      area: String(plan.area || "").slice(0, 160),
      food: String(plan.food || "").slice(0, 160),
      requested_day: ["today", "tomorrow"].includes(plan.requested_day) ? plan.requested_day : "",
      requested_roles: [...new Set((Array.isArray(plan.requested_roles) ? plan.requested_roles : []).filter((role) => ["dinner", "drinks", "live_music"].includes(role)))].slice(0, 3),
      referent_venue_id: String(plan.referent_venue_id || "").slice(0, 120),
      stops: (Array.isArray(plan.stops) ? plan.stops : []).filter((stop) => stop && ["dinner", "drinks", "live_music"].includes(stop.role)).slice(0, 3).map((stop) => ({
        role: stop.role,
        venue_id: String(stop.venue_id || "").slice(0, 120),
        event_id: String(stop.event_id || "").slice(0, 120),
      })),
      shown_venue_ids: safeIds(plan.shown_venue_ids, 12),
      shown_event_ids: safeIds(plan.shown_event_ids, 12),
      area_overrides: Object.fromEntries(Object.entries(plan.area_overrides || {}).filter(([role, area]) => ["dinner", "drinks", "live_music"].includes(role) && typeof area === "string").slice(0, 3).map(([role, area]) => [role, area.slice(0, 160)])),
      proximity_preference: ["sequence", "strong"].includes(plan.proximity_preference) ? plan.proximity_preference : "",
      walking_requested: plan.walking_requested === true,
    },
    event_search: {
      area: String(events.area || "").slice(0, 160),
      requested_day: ["today", "tomorrow"].includes(events.requested_day) ? events.requested_day : "",
      live_music_only: events.live_music_only === true,
      shown_event_ids: safeIds(events.shown_event_ids, 12),
    },
    location: {
      pending_locality: location.pending_locality === true,
      browse_whole_area: location.browse_whole_area === true,
      origin_type: ["venue", "area"].includes(location.origin_type) ? location.origin_type : "",
      origin_venue_id: String(location.origin_venue_id || "").slice(0, 120),
      origin_label: String(location.origin_label || "").slice(0, 160),
      area: String(location.area || "").slice(0, 160),
      pending_origin_phrase: String(location.pending_origin_phrase || "").slice(0, 160),
      pending_category: ["restaurant", "bar", "club", "live_music"].includes(location.pending_category) ? location.pending_category : "",
      pending_nearby: location.pending_nearby === true,
      suggested_venue_id: String(location.suggested_venue_id || "").slice(0, 120),
      pending_task: location.pending_task === "night_plan" ? "night_plan" : "",
      pending_plan_roles: [...new Set((Array.isArray(location.pending_plan_roles) ? location.pending_plan_roles : []).filter((role) => ["dinner", "drinks", "live_music"].includes(role)))].slice(0, 3),
      pending_plan_food: String(location.pending_plan_food || "").slice(0, 160),
      pending_requested_day: ["today", "tomorrow"].includes(location.pending_requested_day) ? location.pending_requested_day : "",
    },
    pending_action: {
      kind: pending.kind === "venue_search" ? "venue_search" : "",
      area: String(pending.area || "").slice(0, 160),
      category: ["restaurant", "bar", "club", "live_music"].includes(pending.category) ? pending.category : "",
      food: String(pending.food || "").slice(0, 160),
      additional: pending.additional === true,
    },
    venues_only: source.venues_only === true,
  };
}

function safeReferent(value = {}) {
  const parameters = Object.fromEntries(
    Object.entries(value.parameters || {})
      .filter(([key, item]) => key.length <= 80 && ["string", "number", "boolean"].includes(typeof item))
      .slice(0, 8)
      .map(([key, item]) => [key, typeof item === "string" ? item.slice(0, 160) : item]),
  );
  return {
    kind: REFERENT_KINDS.includes(value.kind) ? value.kind : "",
    originating_capability: String(value.originating_capability || "").slice(0, 120),
    response_type: String(value.response_type || "").slice(0, 80),
    subjects: (Array.isArray(value.subjects) ? value.subjects : [])
      .filter((item) => item && ["venue", "event"].includes(item.kind) && typeof item.id === "string" && item.id.length > 0 && item.id.length <= 120)
      .slice(0, 6)
      .map((item) => ({ kind: item.kind, id: item.id })),
    allowed_operations: [...new Set((Array.isArray(value.allowed_operations) ? value.allowed_operations : []).filter((item) => REFERENT_OPERATIONS.includes(item)))].slice(0, 10),
    parameters,
    created_at: String(value.created_at || "").slice(0, 48),
    expires_at: String(value.expires_at || "").slice(0, 48),
    turns_remaining: Number.isInteger(value.turns_remaining) ? Math.max(0, Math.min(value.turns_remaining, 3)) : 0,
    member_required: value.member_required === true,
  };
}

export function publicContextEnvelope(value = {}, surface = "public") {
  const legacy = publicConversationContext(value);
  const source = value || {};
  const planVenueIds = legacy.night_plan.stops.map((stop) => stop.venue_id).filter(Boolean);
  const planEventIds = legacy.night_plan.stops.map((stop) => stop.event_id).filter(Boolean);
  const canonical = source.canonical_subjects || {};
  const admin = source.admin_context || {};
  return {
    version: 2,
    surface: surface === "admin" ? "admin" : "public",
    conversation: {
      journey_id: legacy.journey_id,
      language: legacy.language,
      goal: String(source.conversation?.goal || "").slice(0, 120),
      turn_index: Number.isInteger(source.conversation?.turn_index) ? Math.max(0, Math.min(source.conversation.turn_index, 64)) : 0,
    },
    constraints: {
      ...Object.fromEntries(CONTEXT_FIELDS.map((key) => [key, legacy[key]])),
      venues_only: legacy.venues_only,
    },
    recommendation: legacy.recommendation,
    night_plan: legacy.night_plan,
    event_search: legacy.event_search,
    location: legacy.location,
    pending_action: legacy.pending_action,
    canonical_subjects: {
      venues: safeIds([...(canonical.venues || []), ...legacy.recommendation.current_venue_ids, ...planVenueIds], 12),
      events: safeIds([...(canonical.events || []), ...legacy.event_search.shown_event_ids, ...planEventIds], 12),
    },
    pending_referent: safeReferent(source.pending_referent),
    member_context_reference: {
      authenticated: source.member_context_reference?.authenticated === true,
    },
    admin_context: surface === "admin" ? {
      selected_member_id: String(admin.selected_member_id || "").slice(0, 120),
      selected_venue_id: String(admin.selected_venue_id || "").slice(0, 120),
      date_range: String(admin.date_range || "").slice(0, 40),
      report_key: String(admin.report_key || "").slice(0, 120),
      operational_subject: String(admin.operational_subject || "").slice(0, 120),
    } : {
      selected_venue_id: "",
      date_range: "",
      report_key: "",
      operational_subject: "",
    },
  };
}

export function publicReferences(cards = []) {
  const refs = cards.map((card) => {
    if (card.type === "event" && card.event_id) return { kind: "event", id: String(card.event_id) };
    const id = card.canonical_venue_id || card.venue_id;
    return id && ["venue", "directions"].includes(card.type) ? { kind: "venue", id: String(id) } : null;
  }).filter((ref) => ref && ref.id.length <= 120);
  return refs.filter((ref, index) => refs.findIndex((other) => other.kind === ref.kind && other.id === ref.id) === index).slice(0, 6);
}

export function publicChatPayload(message, history = [], context = {}, location = null, clientSessionId = null, inputMode = "text", visitorAction = null) {
  const contextEnvelope = publicContextEnvelope(context, "public");
  const safeInputMode = inputMode === "voice" ? "voice" : "text";
  return {
    ...(visitorAction ? { visitor_action: { operation: visitorAction.operation, version: String(visitorAction.version || ""), role: visitorAction.role || "", venue_id: visitorAction.venue_id || "" } } : {}),
    message: String(message || "").slice(0, 800),
    client_session_id: clientSessionId,
    history: history.slice(-8).map((item) => ({
      role: item.role,
      content: String(item.content || "").slice(0, 800),
      references: item.role === "assistant" ? publicReferences(item.cards || []) : [],
    })),
    conversation_context: publicConversationContext(contextEnvelope),
    context_envelope: contextEnvelope,
    location,
    input_mode: safeInputMode,
    voice_requested: safeInputMode === "voice",
  };
}
